import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

export interface ISavedSearch {
  id: string;
  name: string;
  createdAt: string;
  /** Snapshot of the /search URL's query params at save time. */
  query: Record<string, string>;
}

const STORAGE_KEY = 'cnt-saved-searches';
const STORAGE_IDB_KEY = 'saved-searches:list';

function isValidSavedSearch(s: unknown): s is ISavedSearch {
  const r = s as Partial<ISavedSearch> | null;
  return typeof r?.id === 'string' &&
    typeof r?.name === 'string' &&
    typeof r?.createdAt === 'string' &&
    !!r?.query && typeof r.query === 'object';
}

/** Persists named /search filter snapshots so the visitor can jump back to
 *  "Boondocking on the Olympic Peninsula" without rebuilding every chip.
 *  Mirrors the CookieConsentService SSR-safe storage pattern. */
@Injectable({ providedIn: 'root' })
export class SavedSearchesService {
  private platformId = inject(PLATFORM_ID);

  private readonly store = createIdbKvStore<ISavedSearch[]>({
    legacyKeys: { [STORAGE_IDB_KEY]: STORAGE_KEY },
    reviveLegacy: (_key, parsed) => (Array.isArray(parsed) ? parsed.filter(isValidSavedSearch) : []),
  });

  private readonly _searches$ = new BehaviorSubject<ISavedSearch[]>([]);
  readonly searches$: Observable<ISavedSearch[]> = this._searches$.asObservable();

  constructor() {
    this._searches$.next(this.read());
    if (isPlatformBrowser(this.platformId)) {
      void this.store.init().then(() => this._searches$.next(this.read()));
    }
  }

  list(): ISavedSearch[] {
    return this._searches$.value;
  }

  add(name: string, query: Record<string, string>): ISavedSearch {
    const trimmed = name.trim() || 'Untitled search';
    const entry: ISavedSearch = {
      id: cryptoId(),
      name: trimmed,
      createdAt: new Date().toISOString(),
      query: { ...query },
    };
    const next = [entry, ...this._searches$.value];
    this.write(next);
    return entry;
  }

  remove(id: string): void {
    const next = this._searches$.value.filter(s => s.id !== id);
    this.write(next);
  }

  get(id: string): ISavedSearch | undefined {
    return this._searches$.value.find(s => s.id === id);
  }

  private read(): ISavedSearch[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    return this.store.read(STORAGE_IDB_KEY) ?? [];
  }

  private write(next: ISavedSearch[]): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.write(STORAGE_IDB_KEY, next);
    }
    this._searches$.next(next);
  }
}

/** crypto.randomUUID() with a safe fallback for very old browsers. */
function cryptoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto & { randomUUID(): string }).randomUUID();
  }
  return `ss-${Math.random().toString(36).slice(2, 10)}`;
}
